import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, Check, Clock, TrendingDown, Users, Target } from "lucide-react";
import { Reveal, MaskLines } from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import { SERVICES } from "@/lib/data";
import eseSmall from "@/assets/brand/ese_light_logo.webp";

export default function ServiceDetail() {
  const { slug } = useParams();
  const s = SERVICES.find((x) => x.slug === slug);
  if (!s) return <Navigate to="/services" replace />;
  if (slug === "exit-on-your-terms") return <Navigate to="/services/exit-and-sell" replace />;

  const ese = s.brand === "ese";
  const idx = SERVICES.findIndex((x) => x.slug === slug);
  const next = SERVICES[(idx + 1) % SERVICES.length];

  return (
    <>
      {/* Hero */}
      <section className={`relative grain pt-36 pb-20 md:pt-48 md:pb-28 ${ese ? "bg-blue text-cream" : "bg-cream text-blue"}`}>
        <div className="container-editorial relative z-10">
          <Reveal>
            <Link
              to="/services"
              data-testid="back-to-services"
              className={`inline-flex items-center gap-2 eyebrow text-[0.62rem] mb-10 ${
                ese ? "text-cream/70 hover:text-amber-bright" : "text-blue/60 hover:text-amber-deep"
              } transition-colors`}
            >
              <ArrowLeft size={14} /> All Services
            </Link>
          </Reveal>

          <div className="flex items-center gap-4 mb-6">
            {ese && (
              <div className="bg-cream p-2 shrink-0">
                <img src={eseSmall} alt="Exit Strategy Experts" className="h-12 w-auto" />
              </div>
            )}
            <p className={`eyebrow ${ese ? "text-amber-bright" : "text-amber-deep"}`}>
              {s.eyebrow} · {s.stage}
            </p>
          </div>

          <h1 className="font-serif font-bold text-5xl md:text-7xl lg:text-8xl leading-[0.98] tracking-tight">
            <MaskLines lines={[s.title]} delay={0.1} />
            <MaskLines lines={[s.titleItalic]} className={`italic ${ese ? "text-amber-bright" : "text-amber-deep"}`} delay={0.26} />
          </h1>
          <Reveal delay={0.5}>
            <p className={`font-serif italic text-2xl md:text-3xl mt-8 max-w-2xl ${ese ? "text-cream/80" : "text-blue/75"}`}>
              {s.tagline}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Problem + Who it's for */}
      <section className="bg-cream py-20 md:py-28">
        <div className="container-editorial grid md:grid-cols-2 gap-12 md:gap-20">
          <Reveal>
            <div className="flex items-center gap-3 mb-6">
              <Target size={18} className="text-amber-deep" />
              <p className="eyebrow text-amber-deep">The Problem</p>
            </div>
            <p className="font-serif font-bold text-2xl md:text-3xl text-blue leading-snug">{s.problem}</p>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="flex items-center gap-3 mb-6">
              <Users size={18} className="text-amber-deep" />
              <p className="eyebrow text-amber-deep">Who It's For</p>
            </div>
            <p className="font-body text-lg text-charcoal/75 leading-relaxed">{s.whoFor}</p>
          </Reveal>
        </div>
      </section>

      {/* Deliverables */}
      <section data-testid="deliverables" className={`py-24 md:py-32 grain relative ${ese ? "bg-blue-deep text-cream" : "bg-blue text-cream"}`}>
        <div className="container-editorial relative z-10">
          <Reveal>
            <p className="eyebrow text-amber-bright mb-4">The Deliverables</p>
            <h2 className="font-serif font-bold text-4xl md:text-5xl text-cream mb-14 max-w-2xl">
              Everything included, <span className="italic text-amber-bright">done for you.</span>
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-x-16 gap-y-px">
            {s.deliverables.map((d, i) => (
              <Reveal key={i} delay={(i % 6) * 0.05}>
                <div className="group flex items-start gap-6 py-6 border-b border-cream/12">
                  <span className="font-serif text-2xl text-amber-bright/70 w-10 shrink-0 leading-tight">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <Check size={18} className="text-amber-bright mt-1.5 shrink-0" />
                  <p className="font-body text-cream/85 leading-relaxed">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline + Value */}
      <section className="bg-cream py-20 md:py-28">
        <div className="container-editorial grid md:grid-cols-2 gap-6">
          <Reveal className={`${ese ? "bg-blue" : "bg-blue"} text-cream p-10 md:p-14`}>
            <Clock size={24} className="text-amber-bright mb-6" />
            <p className="eyebrow text-amber-bright mb-4">Timeline</p>
            <p className="font-serif font-bold text-3xl md:text-4xl text-cream leading-tight">{s.timeline}</p>
          </Reveal>
          <Reveal delay={0.1} className={`border ${ese ? "border-blue/20" : "border-blue/15"} p-10 md:p-14 bg-cream`}>
            <TrendingDown size={24} className="text-amber-deep mb-6" />
            <p className="eyebrow text-amber-deep mb-4">Value &amp; Savings</p>
            <p className="font-serif font-bold text-2xl md:text-3xl text-blue leading-snug">{s.valueLine}</p>
            <div className={`mt-8 inline-flex items-center ${ese ? "bg-blue" : "bg-blue"} text-cream px-6 py-3`}>
              <span className="eyebrow text-[0.62rem] text-amber-bright">{s.savings}</span>
            </div>
            <p className="font-body text-sm text-slate mt-5">
              Exact investment quoted on a call, built around your numbers.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Reframe question */}
      <section className={`py-24 md:py-36 grain relative ${ese ? "bg-blue" : "bg-blue-deep"} text-cream`}>
        <div className="container-editorial relative z-10 text-center max-w-4xl mx-auto">
          <Reveal>
            <p className="eyebrow text-amber-bright mb-10">Consider This</p>
            <p className="font-serif italic text-3xl md:text-5xl text-cream leading-snug">
              &ldquo;{s.reframe}&rdquo;
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/contact"
              data-testid="service-detail-cta"
              className="mt-12 inline-flex items-center gap-2 bg-amber-deep text-cream px-9 py-4 eyebrow text-xs hover:bg-cream hover:text-blue transition-colors"
            >
              Start the Conversation <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Next service */}
      <section className="bg-cream py-16">
        <div className="container-editorial">
          <Link
            to={`/services/${next.slug}`}
            data-testid="next-service-link"
            className="group flex items-center justify-between gap-6 border-t border-blue/15 pt-8"
          >
            <span className="eyebrow text-[0.6rem] text-slate">Next · {next.stage}</span>
            <div className="flex items-center gap-4">
              <span className="font-serif font-bold text-2xl md:text-4xl text-blue group-hover:text-amber-deep transition-colors">
                {next.title} <span className="italic">{next.titleItalic}</span>
              </span>
              <ArrowUpRight size={26} className="text-blue/40 group-hover:text-amber-deep group-hover:rotate-45 transition-all duration-400" />
            </div>
          </Link>
        </div>
      </section>

      <CTASection />
    </>
  );
}
